import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/constants";
import { Loader2, Trophy, Inbox, Phone, Mail, Globe } from "lucide-react";

export default function Customers() {
  const navigate = useNavigate();
  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: () => api.get("/customers").then((r) => r.data),
  });

  const total = customers.reduce((a, c) => a + (c.actual_value ?? c.potential_value ?? 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-in">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="h-6 w-6 text-emerald-400" /> Kunden
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{customers.length} gewonnene Kunden</p>
        </div>
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-3 text-right">
          <div className="text-xs text-emerald-300/80">Gesamter Auftragswert</div>
          <div className="text-2xl font-bold text-emerald-300">{fmtEUR(total)}</div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : customers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <Inbox className="h-8 w-8 mx-auto text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">Noch keine Kunden. Verschiebe einen Lead auf „Kunde“, sobald er gewonnen ist.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {customers.map((c) => (
            <div key={c.id} data-testid={`customer-card-${c.id}`}
              onClick={() => navigate(`/leads/${c.id}`)}
              className="rounded-xl border border-border bg-card p-5 cursor-pointer hover:border-emerald-500/40 transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{c.company}</div>
                  <div className="text-xs text-muted-foreground">{c.industry || "—"} · {c.contact_person || "—"}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold text-emerald-400">{fmtEUR(c.actual_value ?? c.potential_value)}</div>
                  <div className="text-[11px] text-muted-foreground">seit {fmtDate(c.close_date)}</div>
                </div>
              </div>
              <div className="mt-3 flex gap-3 text-muted-foreground">
                {c.phone && <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-primary"><Phone className="h-4 w-4" /></a>}
                {c.email && <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-primary"><Mail className="h-4 w-4" /></a>}
                {c.website && <a href={c.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:text-primary"><Globe className="h-4 w-4" /></a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
